import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { message } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { apiListOrderFeedbacks } from '../../services/FeedbackApi';
import {
  TWDataTable,
  TWInput,
  TWSelect,
  EmptyState,
  UserAvatar,
} from '../../components/ui';
import { headerCls, DataTableSkeleton } from '../../data/columns';
import StarRating from './components/StarRating';
import CommentCell from './components/CommentCell';
import { sortableTitle, nextSort } from './components/sortable';

dayjs.extend(utc);

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const STAR_OPTIONS = [
  { value: '', label: 'All ratings' },
  { value: '5', label: '5 stars' },
  { value: '4', label: '4+ stars' },
  { value: '3', label: '3+ stars' },
  { value: '2', label: '2+ stars' },
  { value: '1', label: '1+ stars' },
];

const fmtDate = (v) => (v ? dayjs.utc(v).format('MMM D, YYYY') : '—');

const buildColumns = (sort, onSort) => [
  {
    title: <span className={headerCls}>Order</span>,
    key: 'order',
    render: (_, r) =>
      r.orderNumber ? (
        <Link
          to={`/dashboard/orders/${r.orderId}`}
          className="text-sm font-semibold tabular-nums text-blue-600 hover:underline dark:text-sky-400"
        >
          {r.orderNumber}
        </Link>
      ) : (
        <span className="text-slate-300 dark:text-slate-600">—</span>
      ),
  },
  {
    title: sortableTitle('Store', 'store', sort, onSort),
    key: 'store',
    render: (_, r) => (
      <span className="text-sm text-slate-700 dark:text-slate-300">
        {r.storeName || '—'}
      </span>
    ),
  },
  {
    title: sortableTitle('Customer', 'customer', sort, onSort),
    key: 'customer',
    render: (_, r) => {
      const name = r.customerName || 'Guest';
      return (
        <div className="flex min-w-0 items-center gap-2.5">
          <UserAvatar name={name} size="sm" />
          <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {name}
          </span>
        </div>
      );
    },
  },
  {
    title: sortableTitle('Rating', 'rating', sort, onSort),
    key: 'rating',
    render: (_, r) => <StarRating value={r.starCount || 0} />,
  },
  {
    title: <span className={headerCls}>Comment</span>,
    key: 'comment',
    render: (_, r) => <CommentCell text={r.feedbackText} />,
  },
  {
    title: sortableTitle('Date', 'date', sort, onSort),
    key: 'date',
    render: (_, r) => (
      <span className="text-sm text-slate-600 dark:text-slate-300">
        {fmtDate(r.createDate)}
      </span>
    ),
  },
];

const OrderRatings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minStar, setMinStar] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState({ sortBy: 'date', sortOrder: 'desc' });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    totalRecords: 0,
  });

  // Ham arama girdisini debounce ile fetch değerine taşır.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRows = useCallback(
    async (pageNumber, minstar, searchText, sortState) => {
      setLoading(true);
      try {
        const parts = [
          `?pageNumber=${pageNumber}`,
          `&pageSize=${PAGE_SIZE}`,
          minstar !== '' ? `&minstar=${minstar}` : '',
          searchText ? `&search=${encodeURIComponent(searchText)}` : '',
          `&sortBy=${sortState.sortBy}&sortOrder=${sortState.sortOrder}`,
        ];
        const res = await apiListOrderFeedbacks(parts.join(''));
        setRows(res.data?.data || []);
        setPagination({
          pageNumber,
          pageSize: PAGE_SIZE,
          totalRecords: res.data?.totalRecords || 0,
        });
      } catch {
        setRows([]);
        message.error('Failed to load order ratings');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Filtre/arama/sıralama değişince ilk sayfadan çek.
  useEffect(() => {
    fetchRows(1, minStar, debouncedSearch, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minStar, debouncedSearch, sort, fetchRows]);

  const onSort = (key) => setSort((prev) => nextSort(prev, key));

  const isEmpty = !loading && rows.length === 0;
  const columns = buildColumns(sort, onSort);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-sm">
          <TWInput
            leftIcon={MagnifyingGlassIcon}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, store or order…"
            aria-label="Search order ratings"
          />
        </div>
        <div className="w-full sm:w-44">
          <TWSelect
            value={minStar}
            onChange={setMinStar}
            allowClear={false}
            options={STAR_OPTIONS}
            aria-label="Filter by rating"
          />
        </div>
      </div>

      {loading ? (
        <DataTableSkeleton
          rows={8}
          headers={[
            { label: 'Order', w: 'w-20' },
            { label: 'Store', w: 'w-32' },
            { label: 'Customer', shape: 'avatar', w: 'w-32' },
            { label: 'Rating', w: 'w-28' },
            { label: 'Comment', w: 'w-40' },
            { label: 'Date', w: 'w-24' },
          ]}
        />
      ) : isEmpty ? (
        <EmptyState
          variant="dashed"
          title="No order ratings yet"
          description={
            minStar !== '' || debouncedSearch
              ? 'No ratings match your filters.'
              : 'Customer order ratings will appear here as orders are reviewed.'
          }
        />
      ) : (
        <TWDataTable
          columns={columns}
          data={rows}
          rowKey="id"
          pagination={
            pagination.totalRecords > PAGE_SIZE
              ? {
                  current: pagination.pageNumber,
                  pageSize: PAGE_SIZE,
                  total: pagination.totalRecords,
                  showSizeChanger: false,
                  showTotal: (total, range) =>
                    `Showing ${range[0]} to ${range[1]} of ${total} ratings`,
                  onChange: (page) =>
                    fetchRows(page, minStar, debouncedSearch, sort),
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default OrderRatings;
